import { RootState, buildActiveWebAgentConfigQuery, buildWebAgentSpec, getWebAgentStartUrl, isWebAgentAgent, useGetAiWorkerByIdQuery, useGetAiWorkerConfigurationsQuery, type WebAgentSpec } from "@circuitry-ai/doc-data";
import { UploadedFileType, getUserName, getUserInitials, CtyShowToast, checkType, ErrorBoundary, useCommandPayloadBuilder, extractCommandJsonFromAttrs, validateCommandForm, parseFieldDefs } from "@circuitry-ai/doc-ui";
import { useState, useRef, useEffect, useMemo, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { SharedConverseUI } from "./sharedconverseui";
import { startAgentStream, streamAgentChat } from "./agentstreamingservice";
import { useTranslation } from 'react-i18next';
import { WebAgentConverseLayout } from "./WebAgentConverseLayout";


export interface AgentConverseLayoutProps {
    customAgent?: any,
    customInput?: string,
    showAccordion?: boolean;
    baseUrl?: string;
    onLoadingChange?: (isLoading: boolean) => void;
    /** Called when the agent stream completes. Passes true so parent can run refresh logic. */
    onAgentStreamComplete?: (completed: boolean) => void;
    autoExecute?: boolean;
    hideTopHeader?: boolean;
    commandJson?: Record<string, any>;
    /** Prebuilt web-agent workflow (e.g. from Agent View). When set, config is not re-fetched. */
    webAgentSpec?: WebAgentSpec;
    /** Capture a screenshot after each web-agent step (Execute live preview). */
    liveFrames?: boolean;
}

// Hoist ErrorBoundary-wrapped component to module scope
const SharedConverseUIWithErrorBoundary = ErrorBoundary(SharedConverseUI);

// Helper to safely parse JSON if it's a string
const safeParse = (str: any) => {
    if (typeof str !== 'string') return str;
    try {
        return JSON.parse(str);
    } catch (e) {
        // Invalid JSON string, return null
        console.debug('Failed to parse JSON string:', e);
        return null;
    }
};

// Helper to extract source IDs from an item
const extractSourceIdsFromItem = (item: any): string[] | null => {
    if (item?.pdf_source_ids) return item.pdf_source_ids;
    if (item?.source_ids) return item.source_ids;
    return null;
};

// Helper to extract source IDs from an array
const extractSourceIdsFromArray = (arr: any[]): string[] | null => {
    for (const item of arr) {
        const sourceIds = extractSourceIdsFromItem(item);
        if (sourceIds) return sourceIds;
    }
    return null;
};

// Helper to extract source IDs from an object
const extractSourceIdsFromObject = (obj: Record<string, any>): string[] | null => {
    for (const key of Object.keys(obj)) {
        const agentData = obj[key];
        if (Array.isArray(agentData) && agentData.length > 0) {
            const sourceIds = extractSourceIdsFromItem(agentData[0]);
            if (sourceIds) return sourceIds;
        }
        const sourceIds = extractSourceIdsFromItem(agentData);
        if (sourceIds) return sourceIds;
    }
    return null;
};

// Helper to extract source IDs from action output
const getSourceIdsFromActionOutput = (actionOutput: any) => {
    if (!actionOutput) return null;
    const parsedOutput = safeParse(actionOutput);
    if (!parsedOutput) return null;

    if (Array.isArray(parsedOutput)) {
        return extractSourceIdsFromArray(parsedOutput);
    }

    if (typeof parsedOutput === 'object') {
        return extractSourceIdsFromObject(parsedOutput);
    }

    return null;
};

// Helper function to extract source IDs (adapted from AgentExecutionDataRenderer)
const getSourceIdsFromExecutionData = (execData: any) => {
    if (!execData) return [];

    const outputs = execData?.outputs || {};
    const parsedBody = safeParse(outputs?.body);
    const parsedOutput = safeParse(outputs?.output);

    // Check parsed body for data array (Invoice pdf scanner pattern)
    if (parsedBody?.data && Array.isArray(parsedBody.data)) {
        for (const item of parsedBody.data) {
            if (item?.pdf_source_ids) return item.pdf_source_ids;
        }
    }

    // Check parsed output (processing action outputs pattern)
    if (Array.isArray(parsedOutput)) {
        for (const item of parsedOutput) {
            if (item?.pdf_source_ids) return item.pdf_source_ids;
        }
    }

    return execData?.source_ids ||
        execData?.pdf_source_ids ||
        outputs?.source_ids ||
        outputs?.pdf_source_ids ||
        outputs?.pdf_source_id ||
        getSourceIdsFromActionOutput(outputs?.action_output) ||
        outputs?.action_output?.source_ids ||
        outputs?.action_output?.pdf_source_ids ||
        outputs?.output_data?.source_ids ||
        outputs?.output_data?.pdf_source_ids ||
        execData?.metadata?.source_ids ||
        execData?.metadata?.pdf_source_ids ||
        [];
};

// Helper to normalize source objects and ensure file_extension exists
const normalizeSourceObjects = (sources: any[]) => {
    if (!Array.isArray(sources) || sources.length === 0) return [];

    return sources.map((source: any) => {
        if (!source || typeof source !== 'object') return source;

        let fileExtension = '';

        // 1. Try to extract from file_name
        if (source.file_name) {
            const match = source.file_name.match(/\.([^.]+)$/);
            if (match?.[1]) {
                fileExtension = match[1].toLowerCase();
            }
        }

        // 2. Try to extract from original_file_name
        if (!fileExtension && source.original_file_name) {
            const match = source.original_file_name.match(/\.([^.]+)$/);
            if (match?.[1]) {
                fileExtension = match[1].toLowerCase();
            }
        }

        // 3. Fallback to existing properties if no extension found in names
        if (!fileExtension) {
            fileExtension = source.file_extension || source.element_type || source.invoice_type;
        }

        // Return normalized source with file_extension and element_type
        // KnowledgeSourceUI uses element_type for the API payload's file_extension field
        const finalExtension = fileExtension || 'pdf';
        return {
            ...source,
            file_extension: finalExtension,
            element_type: finalExtension // Force element_type to be the extension
        };
    });
};

// Helper to check if index is valid for message updates (type guard)
const isValidMessageIndex = (idx: number | null, length: number): idx is number => {
    return idx !== null && idx >= 0 && idx < length;
};

// Handler for node_started event
const handleNodeStarted = (
    data: any,
    activeAgentIndexRef: MutableRefObject<number | null>,
    setConverseMessages: Dispatch<SetStateAction<any[]>>
) => {
    if (!data?.title) return;

    setConverseMessages((prev) => {
        const idx = activeAgentIndexRef.current;
        if (!isValidMessageIndex(idx, prev.length)) return prev;
        const updated = [...prev];
        const target = { ...updated[idx] };
        target.phases = [...(target.phases || []), {
            type: 'executing',
            message: `${data.title}`,
            node_id: data.node_id
        }];
        updated[idx] = target;
        return updated;
    });
};

// Handler for node_finished event
const handleNodeFinished = (
    data: any,
    activeAgentIndexRef: MutableRefObject<number | null>,
    setConverseMessages: Dispatch<SetStateAction<any[]>>
) => {
    if (!data?.title || !data?.node_id) return;

    const rawSourceIds = getSourceIdsFromExecutionData(data);
    const sourceIds = normalizeSourceObjects(rawSourceIds);

    // Debug: log normalized sources to verify file_extension
    if (sourceIds && sourceIds.length > 0) {
        console.log('[AgentConverseLayout] Normalized sources from node_finished:', JSON.stringify(sourceIds, null, 2));
    }

    setConverseMessages((prev) => {
        const idx = activeAgentIndexRef.current;
        if (!isValidMessageIndex(idx, prev.length)) return prev;
        const updated = [...prev];
        const target = { ...updated[idx] };

        target.phases = [...(target.phases || []), {
            type: 'completed',
            message: `${data.title}`,
            node_id: data.node_id,
            ...(data.duration_sec != null && { duration_sec: Number(data.duration_sec) })
        }];

        // Populate sources if found in node output
        if (sourceIds && sourceIds.length > 0) {
            target.sources = { source_ids: sourceIds };
        }

        updated[idx] = target;
        return updated;
    });
};

// Handler for workflow_finished event
const handleWorkflowFinished = (
    data: any,
    activeAgentIndexRef: MutableRefObject<number | null>,
    setConverseMessages: Dispatch<SetStateAction<any[]>>,
    setResponseLoading: Dispatch<SetStateAction<boolean>>
) => {
    const rawOutputText = data?.outputs?.text;
    const parsedOutputText = typeof rawOutputText === 'string' ? safeParse(rawOutputText) : rawOutputText;
    const isStructuredOutput =
        parsedOutputText !== null && typeof parsedOutputText === 'object' && !Array.isArray(parsedOutputText);
    // Best-guess "main answer" key order for the textMessage fallback
    const ANSWER_KEYS = ['answer_from_advisor', 'answer', 'output', 'result', 'text'];
    const finalText = isStructuredOutput
        ? String((ANSWER_KEYS.map((k) => parsedOutputText[k]).find((v) => v != null && v !== '') ?? ''))
        : (rawOutputText || data?.outputs?.output || data?.outputs?.answer || data?.text || '');
    const structuredOutput: Record<string, unknown> | undefined = isStructuredOutput
        ? (parsedOutputText as Record<string, unknown>)
        : undefined;
    const messageId = data?.outputs?.message_id;

    const rawSourceIds = getSourceIdsFromExecutionData(data);
    const sourceIds = normalizeSourceObjects(rawSourceIds);

    // Debug: log normalized sources to verify file_extension
    if (sourceIds && sourceIds.length > 0) {
        console.log('[AgentConverseLayout] Normalized sources from workflow_finished:', JSON.stringify(sourceIds, null, 2));
    }

    setConverseMessages((prev) => {
        const idx = activeAgentIndexRef.current;
        if (!isValidMessageIndex(idx, prev.length)) return prev;
        const updated = [...prev];
        const target = { ...updated[idx] };
        target.textMessage = finalText || target.textMessage || '[no response from Agent]';
        target.total_time_taken_seconds = data?.elapsed_time;
        target.message_id = messageId;
        if (structuredOutput) {
            target.structuredOutput = structuredOutput;
        }

        // Populate sources if found
        if (sourceIds && sourceIds.length > 0) {
            target.sources = { source_ids: sourceIds };
        }

        updated[idx] = target;
        return updated;
    });
    setResponseLoading(false);
};

function WebAgentConverseFromAgent(props: Readonly<AgentConverseLayoutProps>) {
    const {
        customAgent,
        customInput,
        showAccordion = false,
        onLoadingChange,
        onAgentStreamComplete,
        hideTopHeader = false,
        commandJson: commandJsonProp,
        webAgentSpec,
        liveFrames = false,
    } = props;
    const location = useLocation();
    const { agent: stateAgent, customInput: stateCustomInput } = location?.state ?? {};
    const agentHint = customAgent ?? stateAgent;
    const token = useSelector((state: RootState) => state.auth.token) as string | null;
    const commandJson = useMemo(
        () => commandJsonProp ?? extractCommandJsonFromAttrs(agentHint?.aiworkers_attrs),
        [commandJsonProp, agentHint?.aiworkers_attrs],
    );
    const needsFullAgent = Boolean(agentHint?.aiworker_id) && !getWebAgentStartUrl(agentHint) && !webAgentSpec?.startUrl;
    const { data: agentById } = useGetAiWorkerByIdQuery(agentHint?.aiworker_id ?? '', {
        skip: !token || !needsFullAgent,
    });
    const agent = needsFullAgent ? (agentById ?? agentHint) : agentHint;
    // Prefer a caller-supplied spec (Agent View already loaded config) so we do not refetch.
    const { data: activeAgentConfig } = useGetAiWorkerConfigurationsQuery(
        buildActiveWebAgentConfigQuery(agentHint?.aiworker_id ?? '') as any,
        { skip: !token || !agentHint?.aiworker_id || Boolean(webAgentSpec) },
    );
    const spec = useMemo(
        () => webAgentSpec ?? buildWebAgentSpec(agent, activeAgentConfig?.configurations?.[0]?.config),
        [webAgentSpec, agent, activeAgentConfig],
    );

    return (
        <WebAgentConverseLayout
            spec={spec}
            agentName={agent?.name}
            aiworkerId={agent?.aiworker_id}
            commandJson={commandJson}
            customInput={customInput ?? stateCustomInput}
            showAccordion={showAccordion}
            hideTopHeader={hideTopHeader}
            liveFrames={liveFrames}
            onLoadingChange={onLoadingChange}
            onAgentStreamComplete={onAgentStreamComplete}
        />
    );
}

function StandardAgentConverseLayout(props: Readonly<AgentConverseLayoutProps>) {

    const { customAgent, customInput, showAccordion = false, baseUrl = import.meta.env.CTY_AGENTS_STREAM_URL, onLoadingChange, onAgentStreamComplete, autoExecute = false, hideTopHeader = false, commandJson: commandJsonProp } = props;
    const { t } = useTranslation();
    const location = useLocation();
    const { agent: stateAgent, customInput: stateCustomInput } = location?.state ?? {};

    const { details: user, isAuthenticated } = useSelector((state: RootState) => state.auth.user);
    const token = useSelector((state: RootState) => state.auth.token) as string | null;
    const [converseMessages, setConverseMessages] = useState<any[]>([]);
    const [sourceId, setSourceId] = useState<Record<string, string>>({});
    const [sessionId, setSessionId] = useState<string>('');
    const [responseLoading, setResponseLoading] = useState<boolean>(false);
    const [addCustomInput, setAddCustomInput] = useState<string>('');

    const activeAgentIndexRef = useRef<number | null>(null);
    const accumulatedPhasesRef = useRef<{ type: string; message: string }[]>([]);
    const streamStartedRef = useRef<boolean>(false);

    const customInputSentRef = useRef<boolean>(false);
    const sendingRef = useRef<boolean>(false);
    const agent = customAgent ?? stateAgent;
    const finalCustomInput = customInput ?? stateCustomInput;

    /** Falls back to the agent's own aiworkers_attrs so callers (e.g. PAL rerun-agent) don't have to extract it themselves. */
    const commandJson = useMemo(
        () => commandJsonProp ?? extractCommandJsonFromAttrs(agent?.aiworkers_attrs),
        [commandJsonProp, agent?.aiworkers_attrs],
    );

    const [commandFormData, setCommandFormData] = useState<Record<string, string>>({});
    const [commandCustomMessage, setCommandCustomMessage] = useState('');
    const buildCommandPayload = useCommandPayloadBuilder();

    // Auto-prefill claim-like fields with the entity ID (e.g. from agent rerun).
    useEffect(() => {
        if (!commandJson || !finalCustomInput?.trim()) return;
        const claimPattern = /^(claim|claim.?number|claimnumber)$/i;
        const claimField = parseFieldDefs(commandJson).find((fd) => claimPattern.test(fd.key));
        if (claimField) {
            setCommandFormData((prev) => prev[claimField.key]?.trim() ? prev : { ...prev, [claimField.key]: finalCustomInput.trim() });
        }
    }, [commandJson, finalCustomInput]);

    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(responseLoading);
        }
    }, [responseLoading, onLoadingChange]);

    // Initialize streaming session when agent tab loads (no UI updates here)
    useEffect(() => {
        let isCancelled = false;
        const initializeStream = async () => {
            try {
                if (streamStartedRef.current) {
                    console.log('Start stream already initiated, skipping');
                    return;
                }
                if (sessionId) {
                    console.log('Session already exists:', sessionId);
                    return;
                }
                if (!agent?.aiworker_id || !token) {
                    console.log('Missing agent or token:', { aiworker_id: agent?.aiworker_id, hasToken: !!token });
                    return;
                }

                console.log('Starting agent stream for:', agent.aiworker_id);
                streamStartedRef.current = true;

                const { session_id } = await startAgentStream({
                    baseUrl,
                    aiworker_id: agent.aiworker_id,
                    token,
                    // ignore chunks here; we only need session id on load
                    // do not pass an abort signal to allow dedupe across StrictMode remounts
                });
                if (session_id && !isCancelled) {
                    console.log('Got session ID:', session_id);
                    setSessionId(session_id);
                } else {
                    console.error('No session ID returned from start-stream');
                    streamStartedRef.current = false;
                }
            } catch (error) {
                console.error('Start stream error:', error);
                streamStartedRef.current = false;
                CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to start agent stream');
            }
        };
        void initializeStream();
        // cleanup resets placeholders and aborts any in-flight request
        return () => {
            isCancelled = true;
            activeAgentIndexRef.current = null;
            accumulatedPhasesRef.current = [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent?.aiworker_id, token]);


    useEffect(() => {
        if (finalCustomInput) {
            setAddCustomInput(finalCustomInput);
        }
    }, [finalCustomInput]);

    useEffect(() => {
        if (customAgent) {
            setConverseMessages([]);
            customInputSentRef.current = false;
            // Reset stream state when agent changes
            streamStartedRef.current = false;
            setSessionId('');
        }

        return () => {
            setConverseMessages([]);
            setSessionId('');
            streamStartedRef.current = false;
        }

    }, [customAgent]);

    // Send preset input only after session is ready. Command agents wait for the user to fill the form attachment first.
    useEffect(() => {
        if (commandJson) return;
        if (addCustomInput && !customInputSentRef.current && sessionId) {
            customInputSentRef.current = true;
            void handleUserInput(addCustomInput, []);
            setAddCustomInput('');
        }
    }, [addCustomInput, sessionId, commandJson]);

    // Common streaming logic extracted to reduce duplication
    const executeAgentStream = async (userInput: string, messageIndex?: number, payloadFields?: Record<string, any>) => {
        try {
            if (sendingRef.current) {
                return;
            }
            sendingRef.current = true;
            setResponseLoading(true);

            // Ensure we have a valid session before sending
            let currentSessionId = sessionId;
            if (!currentSessionId) {
                try {
                    const { session_id } = await startAgentStream({
                        baseUrl,
                        aiworker_id: agent?.aiworker_id,
                        token: token as string,
                    });
                    if (session_id) {
                        setSessionId(session_id);
                        currentSessionId = session_id;
                    } else {
                        throw new Error('Failed to initialize session');
                    }
                } catch (e) {
                    setResponseLoading(false);
                    CtyShowToast(checkType.error, (e as Error)?.message ?? 'Session not ready. Please try again.');
                    return;
                }
            }

            // Reset accumulated phases
            accumulatedPhasesRef.current = [];

            // Handle message preparation based on whether it's a rerun or new input
            if (messageIndex === undefined) {
                // New input: Create placeholder for new response
                setConverseMessages((prev) => {
                    const placeholder = {
                        name: agent?.name,
                        textMessage: '',
                        phases: [] as { type: string; message: string }[],
                        viewLogo: true
                    };
                    activeAgentIndexRef.current = prev.length;
                    return [...prev, placeholder];
                });
            } else {
                // Rerun: Reset the agent response at the specified index
                setConverseMessages((prev) => {
                    const updated = [...prev];
                    if (messageIndex >= 0 && messageIndex < updated.length) {
                        updated[messageIndex] = {
                            name: agent?.name,
                            textMessage: '',
                            phases: [] as { type: string; message: string }[],
                            viewLogo: true,
                            message_id: undefined,
                            sources: undefined,
                            total_time_taken_seconds: undefined
                        };
                        activeAgentIndexRef.current = messageIndex;
                    }
                    return updated;
                });
            }
            onAgentStreamComplete?.(false);
            await streamAgentChat({
                baseUrl,
                aiworker_id: agent?.aiworker_id,
                session_id: currentSessionId,
                user_input: userInput,
                source_id: sourceId,
                token: token as string,
                ...(payloadFields ? { payloadFields } : {}),
                onChunk: (chunk) => {
                    // Handle new Dify API events (for regular agents)
                    if (!chunk?.event) return;

                    const event = chunk.event;
                    const data = chunk.data;

                    if (event === 'node_started') {
                        handleNodeStarted(data, activeAgentIndexRef, setConverseMessages);
                        return;
                    }

                    if (event === 'node_finished') {
                        handleNodeFinished(data, activeAgentIndexRef, setConverseMessages);
                        return;
                    }

                    if (event === 'workflow_finished') {
                        handleWorkflowFinished(data, activeAgentIndexRef, setConverseMessages, setResponseLoading);
                        onAgentStreamComplete?.(true);
                    }
                },
                onError: (e) => {
                    setResponseLoading(false);
                    CtyShowToast(checkType.error, e.message || 'Stream failed');
                }
            });

        } catch (error) {
            setResponseLoading(false);
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Operation failed');
        } finally {
            sendingRef.current = false;
        }
    };

    const handleUserInput = async (userInput: string, uploadedFiles: UploadedFileType[]) => {
        let payloadFields: Record<string, any> | undefined;

        if (commandJson) {
            const result = validateCommandForm(commandJson, commandFormData, commandCustomMessage);
            if (!result.valid) return;
            try {
                payloadFields = await buildCommandPayload(commandJson, commandFormData, commandCustomMessage, userInput);
            } catch (error) {
                // e.g. the SOR token the command asks for couldn't be obtained; nothing is sent
                CtyShowToast(checkType.error, (error as Error)?.message ?? 'Operation failed');
                return;
            }
        }

        const userQuery: any = {
            name: getUserName(isAuthenticated, user),
            textMessage: userInput,
            viewLogo: false,
            initials: getUserInitials(user?.nickname),
            file: uploadedFiles,
        };
        if (commandJson) {
            userQuery.commandFormData = { ...commandFormData };
            userQuery.commandCustomMessage = commandCustomMessage;
        }
        setConverseMessages((prev) => [...prev, userQuery]);

        await executeAgentStream(
            commandJson ? '' : userInput,
            undefined,
            payloadFields,
        );
    };

    const handleRerun = async (userInput: string, messageIndex: number) => {
        // Execute the agent stream with the specified message index for rerun
        await executeAgentStream(userInput, messageIndex);
    };

    const converseProps = {
        name: agent?.name,
        description: agent?.description,
        converseMessages,
        responseLoading,
        handleSend: handleUserInput,
        handleRerun: handleRerun,
        setSourceId,
        showAccordion,
        aiworker_id: agent?.aiworker_id,
        session_id: sessionId,
        hideTopHeader: hideTopHeader,
        commandJson,
        commandFormData,
        commandCustomMessage,
        onCommandFormDataChange: setCommandFormData,
        onCommandCustomMessageChange: setCommandCustomMessage,
    };

    return (
        // The titled section used by the widget and extension brings its own spacing,
        // so the page padding is dropped there to keep the conversation full width.
        <div className="flex h-full flex-col">
            <SharedConverseUIWithErrorBoundary {...converseProps} />
        </div>
    );
}

export function AgentConverseLayout(props: Readonly<AgentConverseLayoutProps>) {
    const location = useLocation();
    const agent = props.customAgent ?? location?.state?.agent;
    if (isWebAgentAgent(agent)) {
        return <WebAgentConverseFromAgent {...props} />;
    }
    return <StandardAgentConverseLayout {...props} />;
}